import { useSuspenseQuery } from "@tanstack/react-query";
import type { ApiErrorResponse, MetricName, Metrics } from "../types";
import { formatLocalDate, normalizeDate } from "../utils/date";
import { METRIC_STALE_TIME } from "../constants";
import { DEFAULT_CAMPAIGN } from "../constants/campaignPerformance";
import { api } from "../utils/apiClient";

export type CampaignPerformanceRequest = {
  from: Date;
  to: Date;
  campaign: string;
  metrics: MetricName[];
};

export type CampaignPerformanceResponse = {
  metrics: Metrics;
};

export type CampaignPerformanceQueryParams = {
  from: Date;
  to: Date;
  metrics: MetricName[];
};

const CAMPAIGN_PERFORMANCE_ENDPOINT = "/api/v1/campaigns/performance";

export const fetchCampaignPerformance = async (
  endpoint: string,
  { to, from, campaign, metrics }: CampaignPerformanceRequest
): Promise<CampaignPerformanceResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.set("from", formatLocalDate(from));
  searchParams.set("to", formatLocalDate(to));
  searchParams.set("campaign", campaign);
  searchParams.set("metrics", metrics.join(","));

  const res = await api.get(`${endpoint}?${searchParams.toString()}`);
  if (!res.ok) {
    let errorMessage = `Failed to load campaign performance: ${res.status}`;
    try {
      const body: ApiErrorResponse = await res.json();
      if (body?.error) {
        errorMessage = body.error;
      }
    } catch {
      // response body not parseable, use default message
    }
    throw new Error(errorMessage);
  }
  return res.json();
};

export const useCampaignPerformanceQuery = ({ from, to, metrics }: CampaignPerformanceQueryParams) => {
  const fromDate = normalizeDate(from);
  const toDate = normalizeDate(to);

  const request: CampaignPerformanceRequest = {
    from: fromDate,
    to: toDate,
    campaign: DEFAULT_CAMPAIGN,
    metrics: [
      "DISCOUNTED_BOOKINGS_SOLD",
      "AVERAGE_DISCOUNT_PER_BOOKING",
      "AVERAGE_DISCOUNT_PERCENTAGE_PER_BOOKING",
      "SEARCH_COVERAGE_PERCENTAGE",
    ] as const,
  };

  return useSuspenseQuery({
    queryKey: [CAMPAIGN_PERFORMANCE_ENDPOINT, fromDate.toISOString(), toDate.toISOString(), DEFAULT_CAMPAIGN],
    queryFn: () => fetchCampaignPerformance(CAMPAIGN_PERFORMANCE_ENDPOINT, request),
    select: (data) => data.metrics.filter((metric) => metrics.includes(metric.metadata.metricName)),
    staleTime: METRIC_STALE_TIME,
  });
};
